import React, { useEffect, useState } from "react";
import { Save, Database, Trash2, GripVertical, Factory, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

export default function Settings() {
  const { can } = useAuth();
  const [pos, setPos] = useState({ base_url: "", configured: false, api_key_masked: "" });
  const [posKey, setPosKey] = useState("");
  const [testRes, setTestRes] = useState(null);
  const [testing, setTesting] = useState(false);
  const [mapping, setMapping] = useState([]);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetText, setResetText] = useState("");
  const [warehouses, setWarehouses] = useState([]);
  const [seedWh, setSeedWh] = useState("none");
  const [categories, setCategories] = useState([]);
  const [catMap, setCatMap] = useState({});
  const [loginCfg, setLoginCfg] = useState({});
  const [branding, setBranding] = useState({ app_name: "", logo_url: "" });
  const [label, setLabel] = useState({ logo: "", paper: "50x30" });
  const [reseller, setReseller] = useState({ whatsapp: "", discount_pct: 0 });

  useEffect(() => {
    api.get("/pos/config").then((r) => setPos(r.data)).catch(() => {});
    api.get("/imports/mapping").then((r) => setMapping(r.data));
    api.get("/products/sync/logs").then((r) => setLogs(r.data.logs || [])).catch(() => {});
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    api.get("/products/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/pos/seed-config").then((r) => { setSeedWh(r.data.warehouse_id || "none"); setCatMap(r.data.category_map || {}); }).catch(() => {});
    api.get("/settings/login").then((r) => setLoginCfg(r.data)).catch(() => {});
    api.get("/settings/branding").then((r) => setBranding(r.data)).catch(() => {});
    api.get("/settings/label").then((r) => setLabel(r.data)).catch(() => {});
    api.get("/settings/reseller").then((r) => setReseller(r.data)).catch(() => {});
  }, []);

  const saveBranding = async () => {
    try {
      await api.put("/settings/branding", branding);
      localStorage.setItem("branding", JSON.stringify(branding));
      document.title = branding.app_name || "Ventedaily";
      toast.success("Branding disimpan");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onLogoFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) return toast.error("Ukuran logo maksimal 500KB");
    const rd = new FileReader();
    rd.onload = () => setBranding((b) => ({ ...b, logo_url: rd.result }));
    rd.readAsDataURL(f);
  };

  const onLoginHeroFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1024 * 1024) return toast.error("Ukuran gambar maksimal 1MB");
    const rd = new FileReader();
    rd.onload = () => setLoginCfg((c) => ({ ...c, hero_image: rd.result }));
    rd.readAsDataURL(f);
  };
  const onLabelLogoFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) return toast.error("Ukuran logo maksimal 500KB");
    const rd = new FileReader();
    rd.onload = () => setLabel((l) => ({ ...l, logo: rd.result }));
    rd.readAsDataURL(f);
  };
  const saveLabel = async () => {
    try { await api.put("/settings/label", label); toast.success("Pengaturan label disimpan"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveReseller = async () => {
    try { await api.put("/settings/reseller", { whatsapp: reseller.whatsapp || "", discount_pct: Number(reseller.discount_pct) || 0, marquee: reseller.marquee || "" }); toast.success("Pengaturan reseller disimpan"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveLogin = async () => {
    try { await api.put("/settings/login", loginCfg); toast.success("Halaman login diperbarui"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const lf = (k) => (e) => setLoginCfg({ ...loginCfg, [k]: e.target.value });

  const saveSeed = async () => {
    try { await api.put("/pos/seed-config", { warehouse_id: seedWh === "none" ? "" : seedWh, category_map: catMap }); toast.success("Gudang default seed disimpan"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const savePos = async () => {
    if (!posKey.trim()) return toast.error("Masukkan API key untuk menyimpan");
    try {
      const r = await api.put("/pos/config", { api_key: posKey });
      setPos({ ...pos, configured: true, api_key_masked: r.data.api_key_masked });
      setPosKey("");
      toast.success("API Key POS disimpan");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const testPos = async () => {
    setTesting(true); setTestRes(null);
    try { const r = await api.post("/pos/test-connection"); setTestRes(r.data); }
    catch (e) { setTestRes({ ok: false, status: "Failed", message: formatApiError(e.response?.data?.detail), endpoint: "/api/v1/integration/ping" }); }
    finally { setTesting(false); }
  };

  const saveMapping = async () => {
    try {
      await api.put("/imports/mapping", mapping.map((m, i) => ({ ...m, order: i + 1 })));
      toast.success("Template mapping disimpan");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const seedSample = async () => {
    setBusy(true);
    try { await api.post("/sample/seed"); toast.success("Data contoh dibuat"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const clearSample = async () => {
    setBusy(true);
    try { await api.delete("/sample/clear"); toast.success("Data contoh dihapus"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resetTx = async () => {
    if (resetText.trim() !== "RESET") return toast.error('Ketik "RESET" untuk konfirmasi');
    setBusy(true);
    try { const r = await api.post("/sample/reset-transactions", { confirm: "RESET" }); toast.success(r.data.message); setResetOpen(false); setResetText(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Konfigurasi POS, template import, dan data contoh" />

      <Tabs defaultValue="branding">
        <TabsList className="bg-white border border-taupe/20">
          <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>
          <TabsTrigger value="pos" data-testid="tab-pos">POS API</TabsTrigger>
          <TabsTrigger value="mapping" data-testid="tab-mapping">Template Mapping</TabsTrigger>
          <TabsTrigger value="sample" data-testid="tab-sample">Data Contoh</TabsTrigger>
          <TabsTrigger value="login" data-testid="tab-login">Halaman Login</TabsTrigger>
          <TabsTrigger value="label" data-testid="tab-label">Product Label</TabsTrigger>
          <TabsTrigger value="reseller" data-testid="tab-reseller">Reseller / Cek Stok</TabsTrigger>
          <TabsTrigger value="online" data-testid="tab-online">Ventedaily Online Sales</TabsTrigger>
          <TabsTrigger value="marketplace" data-testid="tab-marketplace">Marketplace Channel</TabsTrigger>
          {can() && <TabsTrigger value="migrate" data-testid="tab-migrate">Migrasi Legacy</TabsTrigger>}
        </TabsList>

        <TabsContent value="branding">
          <Panel className="p-6 w-full">
            <h3 className="font-heading font-bold text-cocoa text-lg mb-1">Branding Aplikasi</h3>
            <p className="text-sm text-wmuted mb-5">Ganti logo & nama aplikasi (tampil di sidebar & judul tab browser). Perubahan berlaku setelah disimpan.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Nama Aplikasi (Judul Tab Browser)</Label>
                <Input value={branding.app_name || ""} onChange={(e) => setBranding({ ...branding, app_name: e.target.value })} placeholder="Ventedaily" data-testid="branding-app-name" />
                <p className="text-xs text-wmuted mt-1">Muncul di judul tab browser & nama di sidebar.</p>
                <Label className="mt-4 block">Logo (URL) — opsional</Label>
                <Input value={branding.logo_url && !branding.logo_url.startsWith("data:") ? branding.logo_url : ""} onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })} placeholder="https://... atau upload di kanan" data-testid="branding-logo-url" />
              </div>
              <div>
                <Label>Upload Logo (maks 500KB, PNG/SVG/JPG)</Label>
                <Input type="file" accept="image/*" onChange={onLogoFile} data-testid="branding-logo-file" className="cursor-pointer" />
                <div className="mt-3 flex items-center gap-4">
                  <div className="w-20 h-20 rounded-xl bg-cocoa-dark flex items-center justify-center overflow-hidden border border-taupe/20">
                    {branding.logo_url ? <img src={branding.logo_url} alt="logo" className="w-full h-full object-contain p-1.5" data-testid="branding-logo-preview" /> : <span className="text-ivory/60 text-xs">Preview</span>}
                  </div>
                  {branding.logo_url && <Button variant="outline" onClick={() => setBranding({ ...branding, logo_url: "" })} className="border-status-reject text-status-reject" data-testid="branding-logo-clear"><Trash2 className="w-4 h-4 mr-1.5" />Hapus Logo</Button>}
                </div>
              </div>
            </div>
            {can("warehouse_admin") && <Button onClick={saveBranding} data-testid="save-branding-btn" className="mt-6 bg-cocoa hover:bg-cocoa-dark text-ivory"><Save className="w-4 h-4 mr-1.5" /> Simpan Branding</Button>}
          </Panel>
        </TabsContent>

        <TabsContent value="login">
          <Panel className="p-6 max-w-3xl">
            <h3 className="font-heading font-bold text-cocoa text-lg mb-1">Kustomisasi Halaman Login</h3>
            <p className="text-sm text-wmuted mb-5">Ganti gambar & teks yang tampil di halaman login. Perubahan langsung berlaku.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Gambar Hero (URL atau Upload)</Label>
                <Input value={loginCfg.hero_image && !String(loginCfg.hero_image).startsWith("data:") ? loginCfg.hero_image : ""} onChange={lf("hero_image")} placeholder="https://... atau upload di bawah" data-testid="login-cfg-hero-image" />
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <Input type="file" accept="image/*" onChange={onLoginHeroFile} data-testid="login-hero-file" className="cursor-pointer max-w-xs" />
                  {loginCfg.hero_image && <Button variant="outline" onClick={() => setLoginCfg({ ...loginCfg, hero_image: "" })} className="border-status-reject text-status-reject h-9" data-testid="login-hero-clear"><Trash2 className="w-4 h-4 mr-1.5" />Hapus</Button>}
                </div>
                <p className="text-xs text-wmuted mt-1">Rekomendasi: <b>1200 × 1600 px</b> (portrait 3:4), maks <b>1MB</b>. Upload tersimpan di database (base64) — tak perlu hosting eksternal.</p>
                {loginCfg.hero_image && <img src={loginCfg.hero_image} alt="" className="mt-2 h-40 w-full object-cover rounded-lg border border-taupe/20" data-testid="login-hero-preview" />}
              </div>
              <div><Label>Nama Brand</Label><Input value={loginCfg.brand_name || ""} onChange={lf("brand_name")} data-testid="login-cfg-brand-name" /></div>
              <div><Label>Tagline Brand</Label><Input value={loginCfg.brand_tagline || ""} onChange={lf("brand_tagline")} /></div>
              <div className="md:col-span-2"><Label>Judul Hero (kiri)</Label><Input value={loginCfg.hero_title || ""} onChange={lf("hero_title")} data-testid="login-cfg-hero-title" /></div>
              <div className="md:col-span-2"><Label>Subjudul Hero</Label><Input value={loginCfg.hero_subtitle || ""} onChange={lf("hero_subtitle")} /></div>
              <div className="md:col-span-2"><Label>Kutipan Hero</Label><Input value={loginCfg.hero_quote || ""} onChange={lf("hero_quote")} /></div>
              <div><Label>Judul Sambutan</Label><Input value={loginCfg.welcome_title || ""} onChange={lf("welcome_title")} /></div>
              <div><Label>Subjudul Sambutan</Label><Input value={loginCfg.welcome_subtitle || ""} onChange={lf("welcome_subtitle")} /></div>
              <div className="md:col-span-2"><Label>Keterangan Sambutan</Label><Input value={loginCfg.welcome_hint || ""} onChange={lf("welcome_hint")} /></div>
              <div><Label>Teks Tombol Masuk</Label><Input value={loginCfg.button_text || ""} onChange={lf("button_text")} /></div>
              <div><Label>Footer</Label><Input value={loginCfg.footer_text || ""} onChange={lf("footer_text")} /></div>
            </div>
            {can() && <Button onClick={saveLogin} data-testid="save-login-btn" className="mt-5 bg-cocoa hover:bg-cocoa-dark text-ivory"><Save className="w-4 h-4 mr-1.5" /> Simpan Halaman Login</Button>}
          </Panel>
        </TabsContent>

        <TabsContent value="label">
          <Panel className="p-6 max-w-3xl">
            <h3 className="font-heading font-bold text-cocoa text-lg mb-1">Pengaturan Product Label</h3>
            <p className="text-sm text-wmuted mb-5">Logo & ukuran kertas untuk cetak label QR produk (di menu Inventory → Product Label). Logo di sini terpisah dari logo aplikasi.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Ukuran Kertas Label Default</Label>
                <Select value={label.paper || "50x30"} onValueChange={(v) => setLabel({ ...label, paper: v })}>
                  <SelectTrigger data-testid="label-paper-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50x30">50 × 30 mm (label)</SelectItem>
                    <SelectItem value="40x30">40 × 30 mm (label)</SelectItem>
                    <SelectItem value="40x25">40 × 25 mm (label)</SelectItem>
                    <SelectItem value="33x15">33 × 15 mm (label kecil)</SelectItem>
                    <SelectItem value="58">Thermal 58 mm</SelectItem>
                    <SelectItem value="a4">Kertas Biasa (A4/Letter)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-wmuted mt-1">Ukuran dapat diganti lagi saat mencetak. Untuk printer label termal khusus, pilih ukuran fisik label Anda.</p>
              </div>
              <div>
                <Label>Logo Label (maks 500KB, PNG/SVG/JPG)</Label>
                <Input type="file" accept="image/*" onChange={onLabelLogoFile} data-testid="label-logo-file" className="cursor-pointer" />
                <div className="mt-3 flex items-center gap-4">
                  <div className="w-20 h-20 rounded-xl bg-white flex items-center justify-center overflow-hidden border border-taupe/20">
                    {label.logo ? <img src={label.logo} alt="logo" className="w-full h-full object-contain p-1.5" data-testid="label-logo-preview" /> : <span className="text-wmuted text-xs">Preview</span>}
                  </div>
                  {label.logo && <Button variant="outline" onClick={() => setLabel({ ...label, logo: "" })} className="border-status-reject text-status-reject" data-testid="label-logo-clear"><Trash2 className="w-4 h-4 mr-1.5" />Hapus Logo</Button>}
                </div>
                <p className="text-xs text-wmuted mt-1">Kosongkan untuk memakai logo Ventedaily bawaan.</p>
              </div>
            </div>
            {can("warehouse_admin") && <Button onClick={saveLabel} data-testid="save-label-btn" className="mt-6 bg-cocoa hover:bg-cocoa-dark text-ivory"><Save className="w-4 h-4 mr-1.5" /> Simpan Pengaturan Label</Button>}
          </Panel>
        </TabsContent>

        <TabsContent value="reseller">
          <Panel className="p-6 max-w-2xl">
            <h3 className="font-heading font-bold text-cocoa text-lg mb-1">Pengaturan Reseller & Halaman Cek Stok</h3>
            <p className="text-sm text-wmuted mb-5">Dipakai di halaman publik <b>/cek-stok</b> (tanpa login). Nomor WhatsApp untuk tombol "Pesan via WhatsApp" & diskon harga reseller.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Nomor WhatsApp Admin</Label>
                <Input value={reseller.whatsapp || ""} onChange={(e) => setReseller({ ...reseller, whatsapp: e.target.value })} placeholder="628123456789 (format internasional)" data-testid="reseller-whatsapp" />
                <p className="text-xs text-wmuted mt-1">Gunakan format 62xxx (tanpa + atau 0 di depan). Kosongkan untuk pakai share WhatsApp umum.</p>
              </div>
              <div>
                <Label>Diskon Harga Reseller (%)</Label>
                <Input type="number" min={0} max={90} value={reseller.discount_pct ?? 0} onChange={(e) => setReseller({ ...reseller, discount_pct: e.target.value })} data-testid="reseller-discount" />
                <p className="text-xs text-wmuted mt-1">Harga reseller = harga jual − diskon ini. 0 = sama dengan harga jual.</p>
              </div>
            </div>
            <div className="mt-4">
              <Label>Teks Berjalan (Marquee) di Halaman Katalog</Label>
              <Input value={reseller.marquee || ""} onChange={(e) => setReseller({ ...reseller, marquee: e.target.value })} placeholder="Contoh: Promo akhir bulan! Diskon reseller 10% s/d 30 Okt" data-testid="reseller-marquee" />
              <p className="text-xs text-wmuted mt-1">Tampil sebagai tulisan berjalan di halaman publik <b>/katalog</b>. Kosongkan untuk menyembunyikan.</p>
            </div>
            {can("warehouse_admin") && <Button onClick={saveReseller} data-testid="save-reseller-btn" className="mt-6 bg-cocoa hover:bg-cocoa-dark text-ivory"><Save className="w-4 h-4 mr-1.5" /> Simpan Pengaturan Reseller</Button>}
          </Panel>
        </TabsContent>

        <TabsContent value="pos">
          <Panel className="p-6 max-w-2xl">
            <h3 className="font-heading font-bold text-cocoa mb-4">Konfigurasi Sinkronisasi POS</h3>
            <div className="space-y-4">
              <div><Label>Base URL POS (efektif, dari backend)</Label><Input value={pos.base_url || ""} readOnly className="bg-ivory/40" data-testid="pos-url-display" /></div>
              <div><Label>API Key {pos.configured && <span className="text-xs text-status-passed">(tersimpan {pos.api_key_masked})</span>}</Label><Input type="password" value={posKey} onChange={(e) => setPosKey(e.target.value)} placeholder={pos.api_key_masked || "••••••"} data-testid="pos-key-input" /></div>
              <div className="flex gap-2 items-center">
                {can() && <Button onClick={savePos} data-testid="save-pos-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><Save className="w-4 h-4 mr-1.5" /> Simpan API Key</Button>}
                <Button variant="outline" onClick={testPos} disabled={testing} data-testid="test-pos-btn" className="border-taupe text-cocoa">{testing ? "Menguji..." : "Tes Koneksi"}</Button>
              </div>
              {testRes && (
                <div data-testid="pos-test-result" className={`text-sm rounded-md p-3 ${testRes.ok ? "bg-status-passed/10 text-status-passed" : "bg-status-reject/10 text-status-reject"}`}>
                  <div className="font-semibold">{testRes.status || (testRes.ok ? "Connected" : "Failed")}</div>
                  <div className="text-xs opacity-80">{testRes.endpoint}{testRes.checked_at ? ` · ${fmtDate(testRes.checked_at)}` : ""}</div>
                  <div className="text-xs mt-0.5">{testRes.message}</div>
                </div>
              )}
            </div>
            <div className="mt-6">
              <h4 className="font-semibold text-cocoa mb-2 text-sm">Gudang Default Seed (saat sinkron)</h4>
              <p className="text-xs text-wmuted mb-2">Gudang tujuan saat WMS diisi otomatis dari stok POS. Default: Gudang Utama.</p>
              <div className="flex gap-2 max-w-md">
                <Select value={seedWh} onValueChange={setSeedWh}>
                  <SelectTrigger className="bg-white" data-testid="seed-wh-select"><SelectValue placeholder="Pilih gudang" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Gudang Utama (default)</SelectItem>
                    {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {can("warehouse_admin") && <Button onClick={saveSeed} data-testid="save-seed-btn" className="bg-taupe hover:bg-taupe/90 text-white"><Save className="w-4 h-4 mr-1.5" /> Simpan</Button>}
              </div>
              <div className="mt-5">
                <h4 className="font-semibold text-cocoa mb-1 text-sm">Gudang Seed per Kategori (opsional)</h4>
                <p className="text-xs text-wmuted mb-3">Timpa gudang default untuk kategori tertentu. Kategori tanpa pilihan akan memakai gudang default di atas.</p>
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="seed-category-map">
                  {categories.map((c) => (
                    <div key={c} className="flex items-center gap-3">
                      <span className="flex-1 text-sm text-cocoa truncate">{c}</span>
                      <Select value={catMap[c] || "none"} onValueChange={(v) => setCatMap({ ...catMap, [c]: v === "none" ? "" : v })}>
                        <SelectTrigger className="bg-white w-56" data-testid={`seed-cat-${c}`}><SelectValue placeholder="Default" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Pakai default</SelectItem>
                          {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                  {!categories.length && <div className="text-xs text-wmuted">Belum ada kategori. Sinkron master produk dulu.</div>}
                </div>
              </div>
            </div>
            <div className="mt-6">
              <h4 className="font-semibold text-cocoa mb-2 text-sm">Riwayat Sync</h4>
              <div className="space-y-1">
                {logs.slice(0, 5).map((l) => (
                  <div key={l.id} className="text-xs text-wmuted flex justify-between border-b border-taupe/10 py-1">
                    <span>{fmtDate(l.datetime)} · {l.method}</span>
                    <span>{l.new} baru / {l.updated} update / {l.failed} gagal</span>
                  </div>
                ))}
                {!logs.length && <div className="text-xs text-wmuted">Belum ada sync.</div>}
              </div>
            </div>
          </Panel>
        </TabsContent>

        <TabsContent value="mapping">
          <Panel className="p-6 max-w-3xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-cocoa">POS Import Template Mapping</h3>
              {can("warehouse_admin") && <Button onClick={saveMapping} data-testid="save-mapping-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><Save className="w-4 h-4 mr-1.5" /> Simpan</Button>}
            </div>
            <p className="text-sm text-wmuted mb-4">Sesuaikan nama & urutan kolom sesuai template POS Ventedaily.</p>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent"><TableHead className="w-10">#</TableHead><TableHead>Field Sistem</TableHead><TableHead>Nama Kolom Export</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {mapping.map((m, i) => (
                  <TableRow key={m.source}>
                    <TableCell className="text-wmuted"><GripVertical className="w-4 h-4 inline" />{i + 1}</TableCell>
                    <TableCell className="font-mono text-xs">{m.source}</TableCell>
                    <TableCell>
                      <Input value={m.column} onChange={(e) => { const n = [...mapping]; n[i] = { ...n[i], column: e.target.value }; setMapping(n); }} className="h-8" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="sample">
          <Panel className="p-6 max-w-2xl">
            <h3 className="font-heading font-bold text-cocoa mb-2 flex items-center gap-2"><Database className="w-5 h-5" /> Data Contoh</h3>
            <p className="text-sm text-wmuted mb-4">Buat data contoh (produk, penerimaan, dan batch QC) untuk preview. Dapat dihapus kapan saja. Jangan gunakan sebagai data produksi.</p>
            <div className="flex gap-3">
              <Button onClick={seedSample} disabled={busy} data-testid="seed-sample-btn" className="bg-taupe hover:bg-taupe/90 text-white">Buat Data Contoh</Button>
              <Button onClick={clearSample} disabled={busy} variant="outline" data-testid="clear-sample-btn" className="border-status-reject text-status-reject hover:bg-red-50"><Trash2 className="w-4 h-4 mr-1.5" /> Hapus Data Contoh</Button>
            </div>
            {can() && (
              <div className="mt-6 border-t border-taupe/15 pt-4">
                <h4 className="font-semibold text-cocoa text-sm mb-1 flex items-center gap-2"><Trash2 className="w-4 h-4" /> Reset Data Transaksi (Owner)</h4>
                <p className="text-xs text-wmuted mb-3">Menghapus seluruh penerimaan, batch QC, batch import, transfer, stok, dan pergerakan stok — serta data contoh. Master produk hasil sinkron POS TIDAK dihapus. Nomor otomatis (RCV/QC/IMP/TRF) di-reset.</p>
                <Button onClick={() => setResetOpen(true)} disabled={busy} variant="outline" data-testid="reset-tx-btn" className="border-status-reject text-status-reject hover:bg-red-50"><Trash2 className="w-4 h-4 mr-1.5" /> Reset Data Transaksi</Button>
              </div>
            )}
            <Dialog open={resetOpen} onOpenChange={(o) => { setResetOpen(o); if (!o) setResetText(""); }}>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading text-status-reject">Reset Data Transaksi</DialogTitle></DialogHeader>
                <p className="text-sm text-cocoa">Ini akan menghapus <b>semua</b> penerimaan, batch QC, batch import, transfer, stok, dan pergerakan stok — serta data contoh. Master produk hasil sinkron POS <b>tidak</b> dihapus. Tindakan ini <b>tidak bisa dibatalkan</b>.</p>
                <div className="mt-3">
                  <Label>Ketik <span className="font-mono font-bold">RESET</span> untuk konfirmasi</Label>
                  <Input value={resetText} onChange={(e) => setResetText(e.target.value)} placeholder="RESET" className="mt-1.5" data-testid="reset-confirm-input" />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setResetOpen(false)} className="border-taupe text-cocoa">Batal</Button>
                  <Button onClick={resetTx} disabled={busy || resetText.trim() !== "RESET"} data-testid="reset-confirm-btn" className="bg-status-reject hover:bg-red-600 text-white">Hapus Sekarang</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </Panel>
        </TabsContent>

        {can() && <TabsContent value="migrate"><div className="space-y-4"><MigrateLegacy /><LeftoverSalesImport /><StockOpname /><MigrateManufacturing /></div></TabsContent>}
        <TabsContent value="online"><OnlineSalesConfig /></TabsContent>
        <TabsContent value="marketplace"><MarketplaceChannelsConfig /></TabsContent>
      </Tabs>
    </div>
  );
}

const DEFAULT_SQL_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_warehouse-pos-bridge/artifacts/pu8sgva0_vente-prod-jan-2026%20%281%29.sql";

function MigrateLegacy() {
  const [url, setUrl] = useState(DEFAULT_SQL_URL);
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const poll = () => api.get("/admin/migrate-legacy/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { poll(); const t = setInterval(poll, 4000); return () => clearInterval(t); }, []);

  const start = async (dry, tracks) => {
    if (!url.trim()) return toast.error("Isi URL file .sql");
    const isBackfill = Array.isArray(tracks) && tracks.length === 1 && tracks[0] === "backfill_orderid";
    if (!dry && !window.confirm(isBackfill
      ? "Jalankan Backfill Order ID ke database environment ini? Hanya mengisi order_id/marketplace pada sales lama (idempotent)."
      : "Jalankan migrasi PENUH ke database environment ini? Aman diulang (idempotent).")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/migrate-legacy", { sql_url: url, dry_run: dry, tracks });
      if (r.data.ok) toast.success("Migrasi dimulai"); else toast.message(r.data.message);
      poll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const running = status && ["queued", "downloading", "running"].includes(status.status);
  const rep = status?.report?.tracks || {};

  const runUpload = async (dry) => {
    if (!file) return toast.error("Pilih file .sql / .sql.gz dulu");
    if (!dry && !window.confirm("Jalankan migrasi PENUH dari file terunggah? Aman diulang (idempotent).")) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", dry ? "true" : "false");
      const r = await api.post("/admin/migrate-legacy/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data.ok) toast.success("Migrasi dimulai"); else toast.message(r.data.message);
      poll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resetData = async () => {
    const c = window.prompt('PERINGATAN: ini MENGHAPUS semua data transaksi & hasil import di environment ini (produk, user, role, gudang, setting TETAP aman).\n\nKetik persis "HAPUS SEMUA" untuk melanjutkan:');
    if (c == null) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/migrate-legacy/reset", { confirm: c });
      if (r.data.ok) toast.success(`Terhapus ${(r.data.total || 0).toLocaleString("id-ID")} dokumen. Sekarang jalankan Migrasi Penuh.`);
      else toast.error(r.data.message);
      poll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Panel className="p-5 space-y-4" data-testid="migrate-legacy-panel">
      <div>
        <h3 className="font-heading text-cocoa text-lg flex items-center gap-2"><Database className="w-5 h-5" /> Migrasi Data Legacy Ventedaily</h3>
        <p className="text-sm text-wmuted mt-1">Impor data lama (customer, keep, sales, purchasing, dst.) ke database environment ini. Idempotent & aman diulang. Jalankan di produksi setelah deploy untuk mengisi datanya.</p>
      </div>
      <div>
        <Label>URL File .sql (dump MySQL)</Label>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} className="bg-white font-mono text-xs" data-testid="migrate-url" />
      </div>
      <div>
        <Label>Atau unggah file langsung (.sql / .sql.gz)</Label>
        <div className="flex flex-wrap items-center gap-2 mt-1">
          <Input type="file" accept=".sql,.gz" onChange={(e) => setFile(e.target.files?.[0] || null)} className="bg-white max-w-md" data-testid="migrate-file" />
          <Button variant="outline" disabled={busy || running || !file} onClick={() => runUpload(true)} className="border-taupe text-cocoa" data-testid="migrate-upload-dry-btn">Uji File</Button>
          <Button disabled={busy || running || !file} onClick={() => runUpload(false)} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="migrate-upload-run-btn">Unggah &amp; Jalankan Penuh</Button>
        </div>
        {file && <p className="text-xs text-wmuted mt-1">{file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB</p>}
        <p className="text-xs text-wmuted mt-1">Tidak perlu cari URL — cukup pilih file dump dari komputer, lalu klik "Unggah &amp; Jalankan Penuh".</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" disabled={busy || running} onClick={() => start(true)} className="border-taupe text-cocoa" data-testid="migrate-dryrun-btn">Uji (Dry-run)</Button>
        <Button disabled={busy || running} onClick={() => start(false)} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="migrate-run-btn">Jalankan Migrasi Penuh</Button>
        <Button disabled={busy || running} onClick={() => start(false, ["backfill_orderid"])} className="bg-taupe hover:bg-taupe/90 text-white" data-testid="migrate-backfill-orderid-btn">Backfill Order ID</Button>
        <Button variant="outline" disabled={busy || running} onClick={resetData} className="border-status-reject text-status-reject hover:bg-status-reject/10" data-testid="migrate-reset-btn">Reset Data (Mulai Bersih)</Button>
      </div>
      <p className="text-xs text-wmuted -mt-2">Reset Data: <b>menghapus semua data transaksi &amp; hasil import</b> (sales, keep, stok, transfer, PO, settlement, produksi, customer, supplier) di environment ini agar mulai bersih. <b>Master aman</b>: produk, user, role, gudang, routing, setting. Pakai sebelum migrasi penuh untuk memulai periode baru.</p>
      <p className="text-xs text-wmuted -mt-2">Backfill Order ID: mengisi <b>order_id</b> &amp; <b>marketplace</b> ke sales lama dari kolom <code>order_id_marketplace</code> (cepat, tanpa impor ulang item).</p>
      {status && status.status !== "none" && (
        <div className="border border-taupe/20 rounded-lg p-4 bg-ivory" data-testid="migrate-status">
          <div className="flex items-center justify-between text-sm">
            <span>Status: <b className={running ? "text-amber-600" : status.status === "done" ? "text-status-passed" : status.status === "error" ? "text-status-reject" : "text-cocoa"}>{status.status}</b>{status.dry_run ? " (dry-run)" : ""}</span>
            {running && <span className="text-xs text-wmuted">{status.current_track} · {status.step || ""}</span>}
          </div>
          {status.error && <div className="text-xs text-status-reject mt-2">{status.error}</div>}
          {status.status === "done" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3 text-xs">
              {Object.entries(rep).map(([k, v]) => (
                <div key={k} className="bg-white border border-taupe/15 rounded px-2 py-1"><span className="text-wmuted">{k}:</span> {Object.values(v).join(" / ")}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}


function LStat({ label, value, tone }) {
  return <div className="bg-white border border-taupe/15 rounded px-2 py-1.5"><div className="text-wmuted">{label}</div><div className={`font-semibold ${tone || "text-cocoa"}`}>{value}</div></div>;
}

function LeftoverSalesImport() {
  const [file, setFile] = useState(null);
  const [prev, setPrev] = useState(null);
  const [wh, setWh] = useState("");
  const [busy, setBusy] = useState(false);
  const idr = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");

  const doPreview = async () => {
    if (!file) return toast.error("Pilih file .xlsx dulu");
    setBusy(true); setPrev(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/leftover-sales/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPrev(r.data);
      const best = [...(r.data.warehouses || [])].sort((a, b) => b.available - a.available)[0];
      if (best) setWh(best.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doCommit = async () => {
    if (!file || !wh) return toast.error("Cek file & pilih gudang dulu");
    if (prev?.unmatched?.length) return toast.error("Masih ada produk tak cocok");
    if (!window.confirm(`Impor ${prev?.new_sales || 0} penjualan & kurangi stok dari gudang terpilih? Aman diulang (anti-dobel).`)) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("warehouse_id", wh);
      const r = await api.post("/leftover-sales/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Berhasil impor ${r.data.created} penjualan (${r.data.units_deducted} unit dikurangi).`);
      setPrev((p) => ({ ...p, _result: r.data, new_sales: 0 }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Panel className="p-5 space-y-4" data-testid="leftover-import-panel">
      <div>
        <h3 className="font-heading text-cocoa text-lg flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" /> Impor Penjualan Tertinggal (SALE REPORT)</h3>
        <p className="text-sm text-wmuted mt-1">Unggah file Excel <b>SALE REPORT</b> POS lama untuk memasukkan penjualan yang belum termigrasi. Produk dicocokkan otomatis ke varian, stok dikurangi dari gudang pilihan. Aman diulang — nomor SALES yang sudah ada otomatis dilewati.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input type="file" accept=".xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); setPrev(null); }} className="bg-white max-w-md" data-testid="leftover-file" />
        <Button variant="outline" disabled={busy || !file} onClick={doPreview} className="border-taupe text-cocoa" data-testid="leftover-preview-btn">{busy ? "Memproses…" : "Cek File"}</Button>
      </div>
      {file && <p className="text-xs text-wmuted -mt-2">{file.name} · {(file.size / 1024).toFixed(0)} KB</p>}
      {prev && (
        <div className="border border-taupe/20 rounded-lg p-4 bg-ivory space-y-3" data-testid="leftover-preview">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <LStat label="Total Penjualan" value={prev.sales} />
            <LStat label="Baru (akan diimpor)" value={prev.new_sales} tone="text-status-passed" />
            <LStat label="Sudah ada (dilewati)" value={prev.already_imported} tone="text-amber-600" />
            <LStat label="Total Item" value={prev.items} />
            <LStat label="Total Unit" value={prev.total_units} />
            <LStat label="Total Nilai" value={idr(prev.total_amount)} />
            <LStat label="Produk Tak Cocok" value={prev.unmatched?.length || 0} tone={prev.unmatched?.length ? "text-status-reject" : "text-status-passed"} />
          </div>
          {prev.unmatched?.length > 0 ? (
            <div className="text-xs text-status-reject">
              <b>Produk tidak cocok (impor diblokir sampai beres):</b>
              <ul className="list-disc ml-5 mt-1">{prev.unmatched.slice(0, 20).map((u, i) => <li key={i}>{u.name} (x{u.qty})</li>)}</ul>
            </div>
          ) : (
            <div className="text-xs text-status-passed">Semua produk cocok ✓</div>
          )}
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-xs">Kurangi stok dari gudang</Label>
              <Select value={wh} onValueChange={setWh}>
                <SelectTrigger className="w-72 bg-white" data-testid="leftover-wh"><SelectValue placeholder="Pilih gudang" /></SelectTrigger>
                <SelectContent>
                  {(prev.warehouses || []).map((w) => (<SelectItem key={w.id} value={w.id}>{w.name} — tersedia {w.available} (butuh {prev.demand_units})</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <Button disabled={busy || !wh || prev.new_sales === 0 || prev.unmatched?.length > 0} onClick={doCommit} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="leftover-commit-btn">Proses Impor ({prev.new_sales})</Button>
          </div>
          {prev._result && (
            <div className="text-xs bg-white border border-taupe/15 rounded p-2 space-y-1" data-testid="leftover-result">
              <div className="text-status-passed font-medium">Selesai: {prev._result.created} penjualan diimpor · {prev._result.units_deducted} unit dikurangi dari {prev._result.warehouse} · dilewati {prev._result.skipped}.</div>
              {prev._result.shortages?.length > 0 && <div className="text-amber-600">Perhatian: {prev._result.shortages.length} item stoknya kurang (tetap dicatat): {prev._result.shortages.slice(0, 5).map((s) => `${s.product} (butuh ${s.need}, ada ${s.avail})`).join("; ")}{prev._result.shortages.length > 5 ? " …" : ""}</div>}
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

function StockOpname() {
  const [file, setFile] = useState(null);
  const [prev, setPrev] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const idr = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");
  const dl = async () => {
    try { const r = await api.get("/stock-opname/template", { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = "Template_Stok_Opname.xlsx"; a.click(); URL.revokeObjectURL(u); } catch (e) { toast.error("Gagal unduh template"); }
  };
  const doPrev = async () => {
    if (!file) return toast.error("Pilih file dulu"); setBusy(true); setPrev(null); setRes(null);
    try { const fd = new FormData(); fd.append("file", file); const r = await api.post("/stock-opname/preview", fd, { headers: { "Content-Type": "multipart/form-data" } }); setPrev(r.data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doApply = async () => {
    if (!file) return; if (!window.confirm("Terapkan stok opname sebagai baseline? Stok Available di 3 gudang akan DISET = Fisik − Keep (barang yang sedang di-Keep tetap ditahan).")) return; setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await api.post("/stock-opname/apply", fd, { headers: { "Content-Type": "multipart/form-data" } }); setRes(r.data); toast.success(`Opname diterapkan: ${r.data.changed} penyesuaian.`); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Panel className="p-5 space-y-4" data-testid="opname-panel">
      <div><h3 className="font-heading text-cocoa text-lg flex items-center gap-2"><Database className="w-5 h-5" /> Stok Opname (Baseline)</h3>
        <p className="text-sm text-wmuted mt-1">Unduh template berisi daftar barang, isi <b>stok fisik total di rak per gudang</b> (GDG-UTAMA/TOKO/BARU) — termasuk barang yang sedang di-Keep. Sistem otomatis menghitung <b>Available = Fisik − Keep</b> agar barang yang ditahan tidak terhitung ganda.</p></div>
      <div className="flex flex-wrap gap-2 items-center">
        <Button variant="outline" onClick={dl} className="border-taupe text-cocoa" data-testid="opname-template-btn">Unduh Template</Button>
        <Input type="file" accept=".xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); setPrev(null); setRes(null); }} className="bg-white max-w-xs" data-testid="opname-file" />
        <Button variant="outline" disabled={busy || !file} onClick={doPrev} className="border-taupe text-cocoa" data-testid="opname-preview-btn">{busy ? "..." : "Cek Selisih"}</Button>
      </div>
      {prev && (
        <div className="border border-taupe/20 rounded-lg p-4 bg-ivory space-y-3" data-testid="opname-preview">
          <div className="text-xs">Cocok: <b className="text-status-passed">{prev.matched}</b> · Tak cocok: <b className={prev.unmatched_count ? "text-status-reject" : ""}>{prev.unmatched_count}</b> · Selisih nilai: <b>{idr(prev.value_diff)}</b></div>
          <table className="w-full text-xs"><thead><tr className="text-left text-wmuted"><th>Gudang</th><th className="text-right">Fisik</th><th className="text-right">Keep</th><th className="text-right">Available Baru</th><th className="text-right">Sistem</th><th className="text-right">Selisih</th></tr></thead><tbody>
            {Object.entries(prev.warehouses).map(([k, v]) => (<tr key={k}><td className="py-1">{k}</td><td className="text-right">{v.opname}</td><td className="text-right text-amber-600">{v.keep}</td><td className="text-right font-medium">{v.target}</td><td className="text-right">{v.system}</td><td className={`text-right font-semibold ${v.diff < 0 ? "text-status-reject" : v.diff > 0 ? "text-status-passed" : ""}`}>{v.diff > 0 ? "+" : ""}{v.diff}</td></tr>))}
          </tbody></table>
          <div className="text-[11px] text-wmuted">Available Baru = Fisik − Keep. Barang di-Keep tetap ditahan (tidak diubah).</div>
          {prev.unmatched_count > 0 && <div className="text-[11px] text-status-reject">Tak cocok (dilewati): {prev.unmatched.slice(0, 10).join("; ")}{prev.unmatched_count > 10 ? " …" : ""}</div>}
          <Button disabled={busy} onClick={doApply} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="opname-apply-btn">Terapkan Opname (Baseline)</Button>
          {res && <div className="text-xs text-status-passed" data-testid="opname-result">Selesai: {res.changed} penyesuaian diterapkan (batch {String(res.batch).slice(0, 8)}).</div>}
        </div>
      )}
    </Panel>
  );
}

const MFG_LABELS = {
  masters: "Mitra & Penjahit", fabrics: "Kain", accessories: "Aksesoris", hpp_bom: "HPP / BOM",
  purchases: "Pembelian Material", cuttings: "Cutting Order", productions: "Order Jahit",
};

function MigrateManufacturing() {
  const [file, setFile] = useState(null);
  const [dry, setDry] = useState(false);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const poll = () => api.get("/admin/migrate-legacy/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { poll(); const t = setInterval(poll, 3000); return () => clearInterval(t); }, []);

  const run = async () => {
    if (!file) return toast.error("Pilih file .sql / .sql.gz dulu");
    if (!dry && !window.confirm("Jalankan migrasi data manufaktur ke database environment ini? Aman diulang (idempotent). Stok produk retail TIDAK diubah.")) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", dry ? "true" : "false");
      const r = await api.post("/admin/migrate-legacy/manufacturing/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data.ok) toast.success("Migrasi manufaktur dimulai"); else toast.message(r.data.message);
      poll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resetMfg = async () => {
    const c = window.prompt('PERINGATAN: menghapus SEMUA data manufaktur (kain, aksesoris, stok material, BOM/HPP, pembelian material, cutting, order jahit, mitra hasil import). Data POS/retail (produk, penjualan, stok toko) AMAN.\n\nKetik persis "HAPUS PRODUKSI" untuk melanjutkan:');
    if (c == null) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/migrate-legacy/reset-manufacturing", { confirm: c });
      if (r.data.ok) toast.success(`Terhapus ${(r.data.total || 0).toLocaleString("id-ID")} dokumen manufaktur.`);
      else toast.error(r.data.message);
      poll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const isMfg = status && status.kind === "manufacturing";
  const running = isMfg && ["queued", "downloading", "running"].includes(status.status);
  const rep = isMfg ? (status?.report?.tracks || {}) : {};

  return (
    <Panel className="p-5 space-y-4" data-testid="migrate-mfg-panel">
      <div>
        <h3 className="font-heading text-cocoa text-lg flex items-center gap-2"><Factory className="w-5 h-5" /> Migrasi Data Manufaktur (Produksi)</h3>
        <p className="text-sm text-wmuted mt-1">Impor data produksi lama: kain, aksesoris, mitra &amp; penjahit, HPP/BOM, pembelian material, cutting order, dan order jahit. <b>Tidak mengubah stok produk retail.</b> Idempotent &amp; aman diulang. Mendukung file <code>.sql</code> dan <code>.sql.gz</code>.</p>
      </div>
      <div>
        <Label>File dump manufaktur (.sql / .sql.gz)</Label>
        <Input type="file" accept=".sql,.gz" onChange={(e) => setFile(e.target.files?.[0] || null)} className="bg-white" data-testid="migrate-mfg-file" />
        {file && <p className="text-xs text-wmuted mt-1">{file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB</p>}
      </div>
      <label className="flex items-center gap-2 text-sm text-cocoa cursor-pointer">
        <input type="checkbox" checked={dry} onChange={(e) => setDry(e.target.checked)} data-testid="migrate-mfg-dry" /> Uji coba (dry-run — hitung tanpa menyimpan data)
      </label>
      <div className="flex flex-wrap gap-2">
        <Button disabled={busy || running || !file} onClick={run} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="migrate-mfg-run-btn">
          {running ? "Sedang memproses…" : (busy ? "Mengunggah…" : "Unggah & Jalankan Migrasi")}
        </Button>
        <Button variant="outline" disabled={busy || running} onClick={resetMfg} className="border-status-reject text-status-reject hover:bg-status-reject/10" data-testid="migrate-mfg-reset-btn">Reset Data Produksi (Mulai Bersih)</Button>
      </div>
      <p className="text-xs text-wmuted -mt-2">Reset Data Produksi: <b>menghapus semua data manufaktur</b> (kain, aksesoris, stok material, BOM/HPP, pembelian material, cutting, order jahit, mitra hasil import) agar mulai bersih sebelum migrasi. <b>Data POS/retail aman</b> (produk, penjualan, stok toko).</p>
      {isMfg && status.status !== "none" && (
        <div className="border border-taupe/20 rounded-lg p-4 bg-ivory" data-testid="migrate-mfg-status">
          <div className="flex items-center justify-between text-sm">
            <span>Status: <b className={running ? "text-amber-600" : status.status === "done" ? "text-status-passed" : status.status === "error" ? "text-status-reject" : "text-cocoa"}>{status.status}</b>{status.dry_run ? " (dry-run)" : ""}{status.filename ? ` · ${status.filename}` : ""}</span>
            {running && <span className="text-xs text-amber-600 font-medium animate-pulse" data-testid="migrate-mfg-progress">{status.current_track || "memproses…"}{status.step ? ` · ${status.step}` : ""}</span>}
          </div>
          {status.error && <div className="text-xs text-status-reject mt-2 break-words">{status.error}</div>}
          {status.status === "done" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3 text-xs">
              {Object.entries(rep).map(([k, v]) => (
                <div key={k} className="bg-white border border-taupe/15 rounded px-2 py-1">
                  <span className="text-wmuted">{MFG_LABELS[k] || k}:</span> {Object.values(v).join(" / ")}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

function OnlineSalesConfig() {
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [selCustomers, setSelCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/online-sales/config").then((r) => {
      setSelected(r.data.customer_ids || []);
      setSelCustomers(r.data.customers || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/customers", { params: { q } }).then((r) => setResults(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const toggle = (c) => {
    setSelected((p) => (p.includes(c.id) ? p.filter((x) => x !== c.id) : [...p, c.id]));
    setSelCustomers((p) => (p.some((x) => x.id === c.id) ? p : [...p, c]));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/online-sales/config", { customer_ids: selected });
      toast.success("Konfigurasi disimpan");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const Row = (c) => (
    <label key={c.id} className="flex items-center gap-3 px-3 py-2 hover:bg-cocoa/5 cursor-pointer text-sm" data-testid={`online-cust-${c.id}`}>
      <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c)} className="accent-[#6b4f3a] w-4 h-4" />
      <span className="text-cocoa">{c.name}</span>
      <span className="text-wmuted text-xs ml-auto">{c.number}</span>
    </label>
  );

  const selectedRows = selCustomers.filter((c) => selected.includes(c.id));
  const others = results.filter((c) => !selected.includes(c.id));

  return (
    <Panel className="p-6 max-w-2xl" data-testid="online-config-panel">
      <div className="mb-3">
        <div className="font-heading font-bold text-cocoa">Customer Ventedaily Online Sales</div>
        <div className="text-sm text-wmuted">Pilih customer yang transaksinya masuk ke rekap Ventedaily Online Sales (Shopee/TikTok).</div>
      </div>
      <Input placeholder="Cari nama / no. customer..." value={q} onChange={(e) => setQ(e.target.value)} className="mb-3" data-testid="online-customer-search" />
      <div className="border border-taupe/20 rounded-lg max-h-[45vh] overflow-y-auto divide-y divide-taupe/10">
        {selectedRows.length > 0 && (
          <div className="bg-cocoa/5 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-taupe">Terpilih</div>
        )}
        {selectedRows.map(Row)}
        {others.length > 0 && (
          <div className="bg-ivory px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-taupe">Hasil Pencarian</div>
        )}
        {others.map(Row)}
        {selectedRows.length === 0 && others.length === 0 && (
          <div className="text-center text-wmuted py-6 text-sm">{q ? "Tidak ada customer cocok." : "Ketik untuk mencari customer."}</div>
        )}
      </div>
      <div className="flex items-center justify-between mt-3">
        <div className="text-sm text-wmuted">{selected.length} customer dipilih</div>
        <Button onClick={save} disabled={saving} data-testid="save-online-config" className="bg-cocoa hover:bg-cocoa-dark text-ivory">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Menyimpan..." : "Simpan"}
        </Button>
      </div>
    </Panel>
  );
}

function MarketplaceChannelsConfig() {
  const { can } = useAuth();
  const [channels, setChannels] = useState([]);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const editable = can("warehouse_admin");

  useEffect(() => { api.get("/settings/marketplace-channels").then((r) => setChannels(r.data.channels || [])).catch(() => {}); }, []);

  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (channels.some((c) => c.toLowerCase() === v.toLowerCase())) return toast.error("Channel sudah ada");
    setChannels([...channels, v]); setInput("");
  };
  const remove = (i) => setChannels(channels.filter((_, j) => j !== i));
  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/settings/marketplace-channels", { channels }); setChannels(r.data.channels || channels); toast.success("Marketplace channel disimpan"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Panel className="p-6 max-w-2xl" data-testid="marketplace-channels-panel">
      <h3 className="font-heading font-bold text-cocoa text-lg mb-1">Marketplace Channel</h3>
      <p className="text-sm text-wmuted mb-5">Daftar channel untuk <b>Shipping Sale</b> (kasir online). Tambah/hapus channel seperti Shopee, TikTok, Tokopedia, atau custom sendiri.</p>
      {editable && (
        <div className="flex gap-2 mb-4 max-w-md">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Nama channel (mis. Shopee)" className="bg-white" data-testid="mp-channel-input" />
          <Button onClick={add} className="bg-taupe hover:bg-taupe/90 text-white" data-testid="mp-channel-add"><Save className="w-4 h-4 mr-1.5" />Tambah</Button>
        </div>
      )}
      <div className="border border-taupe/20 rounded-lg divide-y divide-taupe/10 max-w-md" data-testid="mp-channel-list">
        {channels.map((c, i) => (
          <div key={i} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`mp-channel-row-${i}`}>
            <span className="text-cocoa">{c}</span>
            {editable && <Button size="icon" variant="ghost" onClick={() => remove(i)} data-testid={`mp-channel-del-${i}`}><Trash2 className="w-4 h-4 text-status-reject" /></Button>}
          </div>
        ))}
        {!channels.length && <div className="text-center text-wmuted py-6 text-sm">Belum ada channel.</div>}
      </div>
      {editable && <Button onClick={save} disabled={saving} className="mt-5 bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="save-mp-channels-btn"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Menyimpan..." : "Simpan Channel"}</Button>}
    </Panel>
  );
}
